import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDeepCompareMemo } from '@clatter/platform';
import { routesNames } from '../routes';
import { FolderManagerMimeTypes } from '../interfaces';
import {
  useGetFolderAssetsQuery,
  useGetSubFoldersQuery,
  useGetFolderPathQuery,
  useUpdateFolderMutation,
  useGetFolderPresentationsQuery,
} from '../services';

//region INTERFACES
interface useFolderManagerProps {
  fileInputRef?: React.RefObject<HTMLInputElement>;
  rootFolderId: number | null;
  selectedFolderId?: number | null;
  uploadUrl?: string;
  inMemory?: boolean;
  onlyFolders?: boolean;
}

//endregion

export const useFolderManager = ({
  rootFolderId,
  selectedFolderId: selectedFromParent,
  inMemory = false,
  onlyFolders = false,
}: useFolderManagerProps) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const location = useLocation();

  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(
    inMemory ? selectedFromParent || rootFolderId : null,
  );

  const folderId = useMemo(() => selectedFolderId || rootFolderId, [selectedFolderId, rootFolderId]);

  const { data: folderBreadcrumbs, isLoading: pathIsLoading } = useGetFolderPathQuery(
    { folderId: +folderId! },
    { skip: !folderId || isNaN(folderId) },
  );

  const { currentData: assetsData, isLoading: assetsDataLoading } = useGetFolderAssetsQuery(
    { parentId: Number(folderId) },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' || onlyFolders },
  );

  const { currentData: subFoldersData, isLoading: subFoldersDataLoading } = useGetSubFoldersQuery(
    {
      parentId: Number(folderId),
    },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' },
  );

  const { currentData: presentationsData, isLoading: presentationsDataLoading } = useGetFolderPresentationsQuery(
    { folderId: Number(folderId) },
    { skip: !folderId || isNaN(folderId) || location.pathname === '/trash' || onlyFolders },
  );

  const [updateFolder] = useUpdateFolderMutation();

  const isLoading = assetsDataLoading || subFoldersDataLoading || pathIsLoading || presentationsDataLoading;

  const hierarchyData = useDeepCompareMemo(() => {
    if (onlyFolders) {
      return subFoldersData || [];
    }

    return [...(subFoldersData || []), ...(presentationsData || []), ...(assetsData || [])];
  }, [subFoldersData, assetsData, presentationsData, onlyFolders]);

  const handleRestoreFolders = async ({ itemsToRestore }: { itemsToRestore: FolderManagerItem[] }) => {
    for (const item of itemsToRestore) {
      if (item.type === FolderManagerMimeTypes.FOLDER) {
        await updateFolder({ folderId: item.id, data: { restore: true } });
      }
    }
  };

  const handleWorkspaceClick = useCallback(({ item }: { item: FolderManagerItem }) => {
    navigate(generatePath(routesNames.editProject, { projectId: item.id.toString() }));
  }, []);

  const handleFolderClick = ({ item }: { item: FolderManagerItem }) => {
    if (inMemory) {
      setSelectedFolderId(item.id);
      return;
    }

    if ('id' in item && slug) {
      navigate(routesNames.folders(slug, item.id.toString()));
    } else {
      console.error('Could not handle folder click! No data provided');
    }
  };
  //endregion

  useEffect(() => {
    if (inMemory && selectedFromParent && !selectedFolderId) {
      setSelectedFolderId(selectedFromParent);
    }
  }, [selectedFromParent, inMemory]);

  return {
    handleFolderClick: handleFolderClick,
    handleRestoreFolders: handleRestoreFolders,
    handleWorkspaceClick: handleWorkspaceClick,
    isLoading: isLoading,
    assetsData: assetsData,
    breadcrumbs: folderBreadcrumbs || [],
    hierarchyData: hierarchyData,
    selectedFolderId: rootFolderId,
  };
};
